"use strict";
/**
 * Market Display UI
 * Handles rendering market information and live data
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.displaySelectedMarket = displaySelectedMarket;
exports.initExtraInfoToggle = initExtraInfoToggle;
exports.updateMarketBidAsk = updateMarketBidAsk;
exports.formatShareCount = formatShareCount;
exports.displayCurrentPosition = displayCurrentPosition;
exports.displayUnknownPosition = displayUnknownPosition;
exports.updatePositionDisplay = updatePositionDisplay;
exports.displayBalance = displayBalance;
const orderbook_display_1 = require("../../shared/orderbook-display");
const side_label_1 = require("./side-label");
const capabilities_1 = require("../venues/capabilities");
function abbreviateFirstWord(label) {
    const trimmed = label.trim();
    if (!trimmed)
        return label;
    const [firstWord, ...rest] = trimmed.split(/\s+/);
    const abbreviatedFirstWord = firstWord.substring(0, 3).toUpperCase();
    return rest.length > 0
        ? `${abbreviatedFirstWord} ${rest.join(" ")}`
        : abbreviatedFirstWord;
}
/**
 * Update selected market display
 */
/**
 * Show or hide the view-only banner for the market's venue.
 *
 * The banner is the real affordance, not the disabled buttons' tooltip: the
 * operator is usually on a touchscreen, where `title` never fires.
 */
function updateViewOnlyBanner(market) {
    const banner = document.getElementById("venue-view-only-banner");
    if (!banner)
        return;
    const reason = (0, capabilities_1.tradingDisabledReason)(market.venue);
    banner.textContent = reason ?? "";
    banner.classList.toggle("hidden", reason === null);
}
function displaySelectedMarket(market) {
    updateViewOnlyBanner(market);
    const titleElement = document.getElementById("selected-market-title");
    const bidElement = document.getElementById("market-bid");
    const askElement = document.getElementById("market-ask");
    if (titleElement)
        titleElement.textContent = market.title;
    if (bidElement)
        bidElement.textContent = market.bid.toFixed(4);
    if (askElement)
        askElement.textContent = market.ask.toFixed(4);
    // Update button labels with team/option names
    const optionALabel = document.getElementById("option-a-label");
    const optionBLabel = document.getElementById("option-b-label");
    const optionALabelSell = document.getElementById("option-a-label-sell");
    const optionBLabelSell = document.getElementById("option-b-label-sell");
    const baseballOptionALabelSell = document.getElementById("bb-option-a-label-sell");
    const baseballOptionBLabelSell = document.getElementById("bb-option-b-label-sell");
    const baseballLabelA = document.getElementById("baseball-label-a");
    const baseballLabelB = document.getElementById("baseball-label-b");
    const basketballLabelA = document.getElementById("basketball-label-a");
    const basketballLabelB = document.getElementById("basketball-label-b");
    const basketballOptionALabelSell = document.getElementById("bk-option-a-label-sell");
    const basketballOptionBLabelSell = document.getElementById("bk-option-b-label-sell");
    const soccerLabelA = document.getElementById("soccer-label-a");
    const soccerLabelB = document.getElementById("soccer-label-b");
    const soccerOptionALabelSell = document.getElementById("sc-option-a-label-sell");
    const soccerOptionBLabelSell = document.getElementById("sc-option-b-label-sell");
    const bidAskSectionLabelYes = document.getElementById("bid-ask-label-yes");
    const bidAskSectionLabelNo = document.getElementById("bid-ask-label-no");
    const manualPriceYesLabel = document.getElementById("manual-price-yes-label");
    const manualPriceNoLabel = document.getElementById("manual-price-no-label");
    // Kalshi binary contracts name only the YES side, so the NO label falls
    // back to "Opposing Team". Venues that label both sides take the other
    // branch — see venues/capabilities.ts.
    //
    // Keyed on the MARKET's venue, not the globally selected one: those agree
    // for the market the operator navigated to, and diverge for any market
    // from elsewhere — where the failure is a NO button reading "Opposing
    // Team" on a venue that knows the real name.
    const namesOnlyYesSide = !(0, capabilities_1.labelsBothSides)(market.venue);
    // In Kalshi mode: use subtitle for first team, "Opposing Team" for second
    // In Polymarket mode: use specific yes/no subtitles
    let yesTeam;
    let noTeam;
    if (namesOnlyYesSide) {
        // Kalshi mode: First header gets yesSubtitle or subtitle, second gets "Opposing Team"
        yesTeam = market.yesSubtitle || market.subtitle || "Option A";
        noTeam = market.noSubtitle || "Opposing Team";
    }
    else {
        // Polymarket mode: Use specific yes/no subtitles
        yesTeam = market.yesSubtitle || "Option A";
        noTeam = market.noSubtitle || "Option B";
        console.log("Market:", market);
    }
    if (optionALabel)
        optionALabel.textContent = yesTeam;
    if (optionBLabel)
        optionBLabel.textContent = noTeam;
    if (optionALabelSell)
        optionALabelSell.textContent = yesTeam;
    if (optionBLabelSell)
        optionBLabelSell.textContent = noTeam;
    if (baseballOptionALabelSell) {
        baseballOptionALabelSell.textContent = abbreviateFirstWord(yesTeam);
    }
    if (baseballOptionBLabelSell) {
        baseballOptionBLabelSell.textContent = abbreviateFirstWord(noTeam);
    }
    if (baseballLabelA) {
        baseballLabelA.dataset.baseLabel = yesTeam;
        (0, side_label_1.setSideLabel)(baseballLabelA, abbreviateFirstWord(yesTeam), yesTeam);
    }
    if (baseballLabelB) {
        baseballLabelB.dataset.baseLabel = noTeam;
        (0, side_label_1.setSideLabel)(baseballLabelB, abbreviateFirstWord(noTeam), noTeam);
    }
    (0, side_label_1.setSideLabel)(basketballLabelA, abbreviateFirstWord(yesTeam), yesTeam);
    (0, side_label_1.setSideLabel)(basketballLabelB, abbreviateFirstWord(noTeam), noTeam);
    if (basketballOptionALabelSell) {
        basketballOptionALabelSell.textContent = abbreviateFirstWord(yesTeam);
    }
    if (basketballOptionBLabelSell) {
        basketballOptionBLabelSell.textContent = abbreviateFirstWord(noTeam);
    }
    (0, side_label_1.setSideLabel)(soccerLabelA, abbreviateFirstWord(yesTeam), yesTeam);
    (0, side_label_1.setSideLabel)(soccerLabelB, abbreviateFirstWord(noTeam), noTeam);
    if (soccerOptionALabelSell) {
        soccerOptionALabelSell.textContent = abbreviateFirstWord(yesTeam);
    }
    if (soccerOptionBLabelSell) {
        soccerOptionBLabelSell.textContent = abbreviateFirstWord(noTeam);
    }
    if (bidAskSectionLabelYes)
        bidAskSectionLabelYes.textContent = yesTeam;
    if (bidAskSectionLabelNo)
        bidAskSectionLabelNo.textContent = noTeam;
    if (manualPriceYesLabel)
        manualPriceYesLabel.textContent = yesTeam;
    if (manualPriceNoLabel)
        manualPriceNoLabel.textContent = noTeam;
}
/**
 * Formats Number:
 * - If < 1000: returns the raw number
 * - If >= 1000: returns truncated 'k' format
 */
function formatNumber(num, decimals = 0) {
    // Millions: 1,000,000+
    if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}m`;
    }
    // Thousands: 1,000 - 999,999
    if (num >= 1000) {
        return `${(num / 1000).toFixed(1)}k`;
    }
    // Under 1,000: Strip trailing .00
    return Number(num.toFixed(decimals)).toString();
}
/**
 * Trim a price to 2 decimals by default, keeping the 3rd/4th decimal only
 * when the price actually has sub-cent precision (0.62 → "0.62",
 * 0.525 → "0.525", 0.5225 → "0.5225").
 */
function trimPrice(price) {
    const str = price.toFixed(4);
    if (str.endsWith("00"))
        return str.slice(0, -2);
    if (str.endsWith("0"))
        return str.slice(0, -1);
    return str;
}
/**
 * Best-price cell: "-" when the side of the book is empty.
 */
function formatBestPrice(price) {
    return price === null ? "-" : trimPrice(price);
}
/**
 * Formats a price like 0.62 → ".62" to save horizontal space.
 */
function formatPrice(price) {
    const str = trimPrice(price);
    return str.startsWith("0.") ? str.slice(1) : str;
}
/**
 * Attach tap-to-toggle handlers on .extra-info rows.
 * Called once at startup.
 *
 * Level price points are shown by default; a tap hides them (and a second
 * tap brings them back) via the `.hide-prices` class.
 */
let extraInfoHandlersAttached = false;
function initExtraInfoToggle() {
    if (extraInfoHandlersAttached)
        return;
    extraInfoHandlersAttached = true;
    document.querySelectorAll(".extra-info").forEach((el) => {
        el.addEventListener("click", () => {
            el.classList.toggle("hide-prices");
        });
    });
}
/**
 * Update market bid/ask with live orderbook data
 * Called when orderbook data updates
 *
 * Best-price cells show the TRUE (un-bucketed) best price; the 3 level rows
 * are server-computed cent buckets with running cumulative dollars.
 * A null side (one-sided book) renders "-" in its cells; data === null
 * renders "-" everywhere.
 */
function updateMarketBidAsk(data, numShares = null) {
    const yesBid = data?.yesBid ?? null;
    const yesAsk = data?.yesAsk ?? null;
    const noBid = data?.noBid ?? null;
    const noAsk = data?.noAsk ?? null;
    // Update main bid/ask prices
    const yesBidElement = document.getElementById("market-bid-yes");
    const yesAskElement = document.getElementById("market-ask-yes");
    const noBidElement = document.getElementById("market-bid-no");
    const noAskElement = document.getElementById("market-ask-no");
    if (yesBidElement)
        yesBidElement.textContent = formatBestPrice(yesBid);
    if (yesAskElement)
        yesAskElement.textContent = formatBestPrice(yesAsk);
    if (noBidElement)
        noBidElement.textContent = formatBestPrice(noBid);
    if (noAskElement)
        noAskElement.textContent = formatBestPrice(noAsk);
    // Apply color scale based on price spreads; a one-sided book has no
    // spread to warn about, so it keeps the default color.
    // Token references, not literals, so the warning colours follow the theme.
    const spreadColor = (bid, ask) => {
        if (bid === null || ask === null)
            return "";
        const spread = Math.round((ask - bid) * 10000) / 10000;
        if (spread >= 0.03)
            return "var(--no)";
        if (spread >= 0.02)
            return "var(--warning)";
        return "";
    };
    const yesColor = spreadColor(yesBid, yesAsk);
    if (yesBidElement && yesAskElement) {
        yesBidElement.style.color = yesColor;
        yesAskElement.style.color = yesColor;
    }
    const noColor = spreadColor(noBid, noAsk);
    if (noBidElement && noAskElement) {
        noBidElement.style.color = noColor;
        noAskElement.style.color = noColor;
    }
    // Render the 3 cent-bucket levels per column
    const display = data?.display;
    const columns = [
        { side: display?.yesBid, prefix: "market-bid-yes-liquid" },
        { side: display?.yesAsk, prefix: "market-ask-yes-liquid" },
        { side: display?.noBid, prefix: "market-bid-no-liquid" },
        { side: display?.noAsk, prefix: "market-ask-no-liquid" },
    ];
    for (const { side, prefix } of columns) {
        for (let i = 0; i < orderbook_display_1.MAX_DISPLAY_LEVELS; i++) {
            const element = document.getElementById(`${prefix}-${i + 1}`);
            if (!element)
                continue;
            const level = side?.levels[i];
            if (level) {
                element.innerHTML = `$${formatNumber(level.dollars)}<span class="level-price"> ${formatPrice(parseFloat(level.price))}</span>`;
            }
            else {
                element.textContent = "-";
            }
        }
    }
    // Update est. cost (per side: needs a share count and that side's ask)
    const estCostAElement = document.getElementById("est-cost-a");
    const estCostBElement = document.getElementById("est-cost-b");
    const estCost = (ask) => numShares !== null && ask !== null
        ? `Est. Cost: $${formatNumber(numShares * ask, 2)}`
        : "Est. Cost: -";
    if (estCostAElement)
        estCostAElement.textContent = estCost(yesAsk);
    if (estCostBElement)
        estCostBElement.textContent = estCost(noAsk);
}
/**
 * A share count for the position line: up to 2 decimals below 1,000 (PM-US
 * contracts are fractional), then 3 significant figures (10.4k, 1.04M).
 * Display only: sells read the exact count.
 */
function formatShareCount(qty) {
    const exact = Number(qty.toFixed(2));
    if (exact < 1000)
        return exact.toString();
    // Rounded first, so 999,950 reads 1M, not 1000k.
    const k = Number((qty / 1e3).toPrecision(3));
    if (k < 1000)
        return `${k}k`;
    return `${Number((qty / 1e6).toPrecision(3))}M`;
}
function legsLabel(note) {
    const legs = note.legs > 0 ? `includes ${note.legs} parallel ${note.legs === 1 ? "leg" : "legs"}` : "";
    if (!note.warn)
        return legs;
    return legs ? `${legs}, may be out of date` : "may be out of date";
}
/**
 * One side's line: the count, then a badge. ⛓N on a held side; ⚠ (amber) on
 * both sides when anything is stale or unknown, since a flat side may be wrong too.
 */
function renderPositionSide(el, side, qty, note) {
    const held = qty > 0;
    const label = side === "yes" ? "YES" : "NO";
    const text = document.createElement("span");
    text.className = "position-text";
    text.textContent = held ? `Position: ${formatShareCount(qty)} ${label}` : "Position: None";
    el.replaceChildren(text);
    const legs = note?.legs ?? 0;
    const warn = note?.warn === true;
    if ((legs > 0 && held) || warn) {
        const badge = document.createElement("span");
        badge.className = `position-legs${warn ? " warn" : ""}`;
        badge.textContent = `${legs > 0 ? `⛓${legs}` : ""}${warn ? "⚠" : ""}`;
        badge.setAttribute("aria-label", legsLabel({ legs, warn }));
        el.appendChild(badge);
    }
    el.className = `position-item ${side}-position ${held ? "held" : "empty"}`;
}
function displayCurrentPosition(yesQuantity, noQuantity, legsNote) {
    const yesPosElement = document.getElementById("market-position-yes");
    const noPosElement = document.getElementById("market-position-no");
    if (yesPosElement)
        renderPositionSide(yesPosElement, "yes", yesQuantity, legsNote);
    if (noPosElement)
        renderPositionSide(noPosElement, "no", noQuantity, legsNote);
}
/** Never loaded: a dash, not "None", which would read as flat. */
function displayUnknownPosition() {
    for (const [id, side] of [["market-position-yes", "yes"], ["market-position-no", "no"]]) {
        const el = document.getElementById(id);
        if (!el)
            continue;
        const text = document.createElement("span");
        text.className = "position-text";
        text.textContent = "Position: —";
        el.replaceChildren(text);
        el.className = `position-item ${side}-position empty`;
    }
}
/**
 * Update position display (called on polling updates)
 */
function updatePositionDisplay(yesQuantity, noQuantity, legsNote) {
    displayCurrentPosition(yesQuantity, noQuantity, legsNote);
}
/**
 * Show balance display
 */
function displayBalance(balance, currency) {
    const balanceElement = document.getElementById("account-balance");
    if (balanceElement) {
        balanceElement.textContent = `${currency} ${balance.toFixed(2)}`;
    }
}
